import convert from "convert-units";
import { round } from "lodash";
import moment from "moment";
import { useState } from "react";
import submit from "submit-form";
import { getEditableValue } from "../../utils/converters/convert-units";
import { goalValidation } from "../../utils/goals-validations";

// Shared by AddGoal.js's own bodyWeight branch and Periodization's
// manage-milestone-goals-modal (BodyweightGoalSection.js) - state,
// prefill, validation and submit-payload assembly for a bodyWeight goal, so
// a fix here (the targetDate UTC-parsing gotcha, the missing
// date-must-be-future check) only ever needs making once. `goal` is the
// existing bodyWeight goal being edited, or null/undefined to create a new
// one. Each caller supplies its own `buildUrl(id)` since they hit different
// actions with different close-vs-stay-open behavior on save (AddGoal.js
// closes the modal, manage-milestone-goals-modal keeps it open).
export const useBodyweightGoalForm = ({
  goal,
  units,
  assert,
  flash,
  buildUrl,
}) => {
  // 1 decimal, not 2 - matches getBodywweightAndUnit's own rounding (what a
  // read-only row showing this same goal already displays), so an edit form
  // never shows more precision than that row did.
  const initialTargetWeight = goal
    ? getEditableValue(goal.details.targetWeight, units, 1)
    : "";
  const initialTargetDate = goal
    ? // Explicit format, not moment's ISO auto-detect - a bare "YYYY-MM-DD"
      // string (targetDate has no time component) parses as UTC midnight
      // there, which .toDate() then reads back a day behind in any
      // timezone west of UTC. The explicit format parses in local time
      // instead, matching what was actually picked.
      moment(goal.targetDate, "YYYY-MM-DD").toDate()
    : moment().add(1, "month").toDate();

  const [targetWeight, setTargetWeight] = useState(initialTargetWeight);
  const [targetUnits, setTargetUnits] = useState(units);
  const [targetDate, setTargetDate] = useState(initialTargetDate);

  // Stable across re-renders since goal/units don't change while this form
  // is open (nothing here triggers a page refresh until Save is clicked) -
  // callers combine this with their own "required fields filled" check for
  // their own Save button, since not every caller disables Save on no-op
  // (AddGoal.js never has; manage-milestone-goals-modal does).
  const isUnchanged =
    !!goal &&
    targetUnits === units &&
    String(targetWeight) === String(initialTargetWeight) &&
    moment(targetDate).isSame(initialTargetDate, "day");

  const submitBodyweightGoal = () => {
    const convertedWeight =
      targetUnits === "metric"
        ? targetWeight
        : round(convert(targetWeight).from("lb").to("kg"), 5);
    const details = { targetWeight: convertedWeight };
    const utcTargetDate = Number(moment(targetDate).format("YYYYMMDD"));

    if (!goalValidation("bodyWeight", details, assert, flash, utcTargetDate)) {
      return;
    }

    const data = {
      startDate: moment().format("YYYY-MM-DD"),
      utcStartDate: Number(moment().format("YYYYMMDD")),
      type: "bodyWeight",
      details,
      utcTargetDate,
      targetDate: moment(targetDate).format("YYYY-MM-DD"),
    };

    submit(buildUrl(goal?._id), { method: "POST", body: data });
  };

  return {
    targetWeight,
    setTargetWeight,
    targetUnits,
    setTargetUnits,
    targetDate,
    setTargetDate,
    isUnchanged,
    submitBodyweightGoal,
  };
};
